import { ReferenceChecks } from '@seek/profile-read-ui';
import { useTranslations } from '@vocab/react';
import { Button, Inline, Stack } from 'braid-design-system';
import { useState } from 'react';

import { Card } from 'src/components/Card/Card';
import { LoadingCardItem } from 'src/components/LoadingBlock/LoadingBlock';
import { REFERENCES } from 'src/components/Router';
import { useAnalytics } from 'src/modules/analytics/AnalyticsProvider';

import translations from './.vocab';
import { AddReferenceDialog } from './AddReferenceDialog/AddReferenceDialog';
import { ReferenceDrawer } from './ReferenceDrawer/ReferenceDrawer';
import { useReferenceChecks } from './data';
import type { Reference } from './types';

export const References = () => {
  const { referenceChecks, loading, error, addReferenceButtonEnabled } =
    useReferenceChecks();
  const [showAddReferenceDialog, setShowAddReferenceDialog] = useState(false);

  const [showDrawer, setShowDrawer] = useState<boolean>(false);
  const [activeReference, setActiveReference] = useState<Reference | undefined>(
    undefined,
  );

  const { addReferencePressed, cardOpened } = useAnalytics();

  const { t } = useTranslations(translations);
  const isEmpty = referenceChecks?.length === 0;

  const onEditCard = (id: string) => {
    const [referenceCheckId, indexStr] = id?.split('~') || [];
    const index = indexStr ? Number(indexStr) : 0;
    const reference = referenceChecks?.find(
      (refCheck) => refCheck.id === referenceCheckId,
    )?.references[index];
    setActiveReference(reference);
    setShowDrawer(true);
    cardOpened({
      currentPage: 'profile',
      currentPageSection: 'references',
      currentPageSubsection: 'references',
      currentPageSectionState: 'edit',
    });
  };

  const addReferenceClick = () => {
    setShowAddReferenceDialog(true);

    addReferencePressed();
  };

  const cardAction = (
    <Inline space="none">
      <Button
        variant="ghost"
        tone="formAccent"
        type="button"
        onClick={addReferenceClick}
      >
        {t('Add reference')}
      </Button>
    </Inline>
  );

  return (
    <>
      <AddReferenceDialog
        showAddReferenceDialog={showAddReferenceDialog}
        setShowAddReferenceDialog={setShowAddReferenceDialog}
      />
      <Card
        name={REFERENCES}
        title={t('References')}
        emptyCardText={t('Add references to give potential...')}
        isEmpty={isEmpty}
        cardAction={addReferenceButtonEnabled ? cardAction : null}
        loading={loading || Boolean(error)}
        loader={
          <Stack space="small">
            <LoadingCardItem />
            <LoadingCardItem />
          </Stack>
        }
      >
        <ReferenceChecks
          referenceChecks={referenceChecks ?? []}
          onEditCard={onEditCard}
        />
      </Card>
      <ReferenceDrawer
        isOpen={showDrawer}
        onClose={() => setShowDrawer(false)}
        reference={activeReference}
      />
    </>
  );
};
